/**
 * The policy as the file it is: the PDF in the browser's own viewer, served
 * through the tenant-guarded download route with an inline disposition. There
 * is no extracted-text rendering, because the PDF is the record and a
 * re-typeset copy of it is not.
 *
 * Reading the document is an action, not a tab: the page header's View
 * document opens this wide sheet over whatever tab is showing. The sheet's
 * popup only mounts while open, so the PDF is fetched when someone asks for
 * it and never on page entry.
 *
 * Nothing is rendered client-side on purpose — the browser's viewer already
 * does search, zoom and page navigation better than anything worth shipping
 * here, and the bytes never leave this origin.
 *
 * @module components/regulation/policy/PolicyDocumentPreview
 */
import { ExternalLinkIcon, FileWarningIcon } from "lucide-react";

import { policyVersion, usePolicies } from "../../../state/policies";
import { Button } from "../../ui/button";
import { Sheet, SheetHeader, SheetPopup, SheetTitle } from "../../ui/sheet";
import { ListSkeleton } from "../LoadingSkeletons";

function PolicyDocumentViewer({
  organizationId,
  policyId,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
}) {
  const { snapshot, isPending, previewUrl } = usePolicies(organizationId);
  const policy = snapshot?.policies.find((candidate) => candidate.id === policyId) ?? null;
  const src = previewUrl(policyId);

  if (policy === null || src === null) {
    return (
      <>
        <SheetHeader>
          <SheetTitle>Document</SheetTitle>
        </SheetHeader>
        <div className="px-6 pb-6">
          {isPending ? (
            <ListSkeleton rows={4} />
          ) : (
            <div className="rounded-xl border border-dashed border-border/60 px-6 py-12 text-center">
              <FileWarningIcon className="mx-auto size-5 text-muted-foreground/60" />
              <p className="mt-3 text-sm font-medium text-foreground">Document not found</p>
              <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                This policy is not in your organization's library.
              </p>
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {/* pe-14 keeps the header clear of the sheet's close button. */}
      <SheetHeader className="flex-row items-center justify-between gap-3 py-4 pe-14 max-sm:pb-4">
        <div className="flex min-w-0 items-center gap-2">
          <SheetTitle className="truncate text-base">{policy.filename}</SheetTitle>
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-foreground/70">
            v{policyVersion(policy)}
          </span>
        </div>
        {/* Download lives in the page header; repeating it here would stack
            three download affordances (header, this row, the viewer's own). */}
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="shrink-0 cursor-pointer gap-1.5"
          render={<a href={src} target="_blank" rel="noopener noreferrer" />}
        >
          <ExternalLinkIcon className="size-3.5" />
          Open in new tab
        </Button>
      </SheetHeader>
      {/* The frame takes the rest of the sheet's height, so the viewer — not
          the sheet — is what scrolls. */}
      <div className="min-h-0 flex-1 px-4 pb-4">
        <iframe
          src={src}
          title={policy.filename}
          className="size-full rounded-xl border border-border/60 bg-muted"
        />
      </div>
    </>
  );
}

/** The document in a wide sheet; controlled so the header action and a `#document` link both open it. */
export function PolicyDocumentPreview({
  organizationId,
  policyId,
  open,
  onOpenChange,
}: {
  readonly organizationId: string | null;
  readonly policyId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetPopup side="right" className="h-full max-w-[min(1100px,calc(100vw-3rem))]">
        <PolicyDocumentViewer organizationId={organizationId} policyId={policyId} />
      </SheetPopup>
    </Sheet>
  );
}
